"use client";

import React, { useCallback, useEffect, useState } from "react";
import { Chip } from "./Chip";
import { Icon } from "./Icon";
import { GlobalTag, ThemeVariant } from "@/types";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

interface ChipGroupProps {
  label?: string;
  variant?: ThemeVariant;
  tags: GlobalTag[];
  onChange?: (activeTags: string[]) => void;
  canSelectNone?: boolean;
  defaultActiveTags?: string[];
  showResetButton?: boolean;
  searchParamName?: string;
}

export const ChipFilterGroup: React.FC<ChipGroupProps> = ({
  label,
  variant,
  tags,
  onChange,
  canSelectNone = false,
  defaultActiveTags = [],
  showResetButton = false,
  searchParamName = "filter",
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchParamValue = searchParams.get(searchParamName);
  const [activeTags, setActiveTags] = useState<string[]>(
    searchParamValue ? [searchParamValue] : defaultActiveTags
  );

  // Sync the parent with the URL-derived filter once on mount; skip when
  // falling back to defaultActiveTags since the parent already owns that value.
  useEffect(() => {
    if (searchParamValue !== null) onChange?.([searchParamValue]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createQueryString = useCallback(
    (name: string, value: string | null) => {
      const params = new URLSearchParams(searchParams.toString());
      if (value) params.set(name, value);
      else params.delete(name);

      return params.toString();
    },
    [searchParams]
  );

  function updateSearchParams(value: string | null) {
    router.replace(pathname + "?" + createQueryString(searchParamName, value), {
      scroll: false,
    });
  }

  const selectTag = (id: string) => {
    const next =
      activeTags.length === 1 && activeTags[0] === id && canSelectNone
        ? []
        : [id];

    updateSearchParams(next.length === 0 ? null : id);
    setActiveTags(next);
    onChange?.(next);
  };

  const resetTags = () => {
    setActiveTags([]);
    onChange?.([]);
    updateSearchParams(null);
  };

  return (
    <div className="col-span-full flex flex-row flex-wrap gap-xs sm:gap-sm mb-lg items-center">
      {label && (
        <span
          className={`w-full font-weight-medium ${variant === "light" ? "text-white" : "text-black"}`}
        >
          {label}
        </span>
      )}
      {tags.map((tag) => (
        <Chip
          key={tag.id}
          color={variant === "light" ? "white" : "black"}
          active={activeTags.includes(tag.id)}
          onClick={() => selectTag(tag.id)}
          className={
            "text-sm font-medium px-3! py-1! sm:px-4! sm:py-1.5! flex flex-row justify-start items-center hover:text-white " +
            (variant === "dark" ? "hover:bg-purple-dark! " : "") +
            (variant === "dark" && activeTags.includes(tag.id)
              ? "text-white! bg-purple!"
              : "")
          }
        >
          {tag.label}
          {canSelectNone && (
            <span
              className={`overflow-hidden transition-all duration-200 ease-in-out flex items-center ${activeTags.includes(tag.id) ? "ml-xs w-4 opacity-100" : "ml-0 w-0 opacity-0"}`}
            >
              <Icon name="agent-close" size="16" decorative />
            </span>
          )}
        </Chip>
      ))}
      {showResetButton && canSelectNone && activeTags.length > 0 && (
        <button
          onClick={resetTags}
          className="px-sm py-xs text-sm font-medium text-red-500 hover:underline"
        >
          Filter zurücksetzen
        </button>
      )}
    </div>
  );
};
